import type { MetaFunction } from "@remix-run/node";

export const meta: MetaFunction = () => {
  return [
    { title: "Privacy Policy - WayPick" },
    {
      name: "description",
      content:
        "Learn how WayPick collects, uses, and protects your information when building smart, personalized routes for your day.",
    },
  ];
};

export default function LegalPrivacyPolicy() {
  return (
    <div className="max-w-3xl mx-auto p-6 text-gray-800">
      <h1 className="text-3xl font-bold mb-4">Privacy Policy</h1>
      <p className="mb-4">Last updated: August 11, 2025</p>

      <p className="mb-4">
        This Privacy Policy describes how information is collected, used, and disclosed when you use the WayPick
        application (the “App”).
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">1. Purpose</h2>
      <p className="mb-4">
        WayPick helps users discover and plan personalized routes for visiting points of interest. In order to provide
        this functionality,
        we collect and process certain types of information from users, including search location and preferences.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">2. Information We Collect</h2>
      <p className="mb-2">We may collect the following information when you use the App:</p>
      <ul className="list-disc list-inside mb-4">
        <li><strong>Current location:</strong> your current location to suggest nearby points of interest and build
          routes.
        </li>
        <li><strong>Search location:</strong> the area where you want to explore attractions.</li>
        <li><strong>Search radius:</strong> how far from the selected point you wish to find suggestions.</li>
        <li><strong>Preferred transport mode:</strong> walking, biking, driving, etc.</li>
        <li><strong>Types of attractions:</strong> e.g., museums, parks, architecture, etc.</li>
        <li><strong>Preferred language:</strong> to tailor route instructions or content accordingly.</li>
      </ul>
      <p className="mb-4">
        All of this information is used solely to generate personalized route suggestions and is not associated with
        your identity.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">3. Use of AI Services</h2>
      <p className="mb-4">
        WayPick may use artificial intelligence services provided by third-party vendors such as OpenAI to process
        certain user queries,
        generate route suggestions, or provide language-specific content. When you interact with these features, parts
        of your input
        (such as your search preferences, selected attractions, or natural language requests) may be sent to these external
        services for processing.
      </p>
      <p className="mb-4">
        We do not use this data to identify users, and OpenAI does not store or use it to train its models. For more
        information, see OpenAI’s privacy policy:
        <a
          href="https://openai.com/policies/privacy-policy"
          className="text-blue-600 underline ml-1"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://openai.com/policies/privacy-policy
        </a>
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">4. Analytics</h2>
      <p className="mb-4">
        We use <strong>Google Analytics</strong> to collect anonymous usage statistics, such as user behavior patterns
        and general usage trends.
        This helps us improve the performance and functionality of the App. The collected data is aggregated and does
        not personally identify users.
        Learn more about Google’s data usage here:
        <a
          href="https://policies.google.com/technologies/partner-sites"
          className="text-blue-600 underline ml-1"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://policies.google.com/technologies/partner-sites
        </a>
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">5. Data Sharing</h2>
      <p className="mb-4">
        We do not sell or share your information with third parties, except as required for the analytics and AI
        services mentioned above,
        or to comply with legal obligations. Any data shared in these contexts is anonymous and does not personally
        identify individual users.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">6. Security</h2>
      <p className="mb-4">
        We take reasonable technical and organizational measures to protect your information. All data exchanged between
        your device and our servers
        is encrypted and transmitted over secure HTTPS connections. Additionally, API requests (including RESTful
        requests) are handled securely,
        and access is restricted according to industry best practices.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">7. Changes to This Policy</h2>
      <p className="mb-4">
        We may update this Privacy Policy from time to time. Any updates will be posted here with the revised date
        indicated at the top.
      </p>

      <h2 className="text-xl font-semibold mt-6 mb-2">8. Contact Us</h2>
      <p className="mb-4">
        If you have any questions or concerns about this Privacy Policy, please{" "}
        <a
          href="#tally-open=w2992j&tally-emoji-text=👋&tally-emoji-animation=wave"
          className="text-blue-600 underline"
        >
          contact us
        </a>
      </p>
    </div>
  );
}
